import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { formatLPA } from '@kollez/shared';

interface Stats {
  stats: {
    batchYear: number;
    totalStudents: number;
    placed: number;
    placementRate: number;
    avgCtc: number;
    maxCtc: number;
  } | null;
}

/**
 * /stats — placement stats for the student's own cohort.
 *
 * Deep-audit fixes (Page 24/N):
 *   - The KPI labeled 'Median CTC' pulled from Stats.avgCtc. But the
 *     API pipeline computes { $avg: '$ctcInr' } (mean, not median). A
 *     student trusting the label 'Median' would misread the distribution:
 *     mean and median diverge sharply when a few six-figure offers pull
 *     the average up. Relabeled to 'Average CTC' to match what the API
 *     actually calculates.
 *   - Helper line claimed 'Companies visible on this page…' — but no
 *     companies are listed on this page (it shows aggregate stats only).
 *     Removed; the sentence contradicted the surface.
 */
export function StudentStatsPage() {
  const query = useQuery({
    queryKey: ['student', 'me', 'stats'],
    queryFn: () => apiGet<Stats>('/students/me/stats'),
  });

  const s = query.data?.stats;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Get ready</p>
          <h1 className="h-page">Placement stats</h1>
          <p className="subhead">How your batch is doing so far. Numbers update whenever a fresh offer is accepted.</p>
        </div>
      </div>

      <AsyncBoundary
        kind="kpis"
        loading={query.isLoading}
        error={query.error}
        empty={!s}
        emptyTitle="No stats yet"
        emptyHint="Once a few students in your batch accept offers, this page fills in."
      >
        {s && (
          <>
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
              <div className="card kpi">
                <span className="kpi-label">Placement rate</span>
                <span className="kpi-value">{s.placementRate}%</span>
                <span className="kpi-meta">{s.placed} of {s.totalStudents} in batch {s.batchYear}</span>
              </div>
              <div className="card kpi" data-tone="success">
                <span className="kpi-label">Average CTC</span>
                <span className="kpi-value">{s.avgCtc > 0 ? formatLPA(s.avgCtc) : '—'}</span>
                <span className="kpi-meta">mean across accepted offers</span>
              </div>
              <div className="card kpi">
                <span className="kpi-label">Highest CTC</span>
                <span className="kpi-value">{s.maxCtc > 0 ? formatLPA(s.maxCtc) : '—'}</span>
              </div>
              <div className="card kpi">
                <span className="kpi-label">Batch year</span>
                <span className="kpi-value">{s.batchYear}</span>
              </div>
            </div>
            <p className="helper">Only your batch and college are included in these numbers.</p>
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
